import { NETWORK_MENUS, APP_URLS, PROFILE_LINKS } from './header-config.js';

// Accept only web destinations, not executable URL schemes.
function webURL(value) {
  if (!value || typeof value !== 'string') return null;
  try {
    const url = new URL(value, location.href);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
  } catch { return null; }
}

/** Shared controller for the application, Network and profile dropdowns. */
export function createHeader({ onNotice = () => {} } = {}) {
  const networkBar = document.querySelector('#network-menu-bar');
  const popoverRoot = document.querySelector('#menu-popovers');
  const profileButton = document.querySelector('#profile-button');
  const profilePopover = document.querySelector('#profile-popover');
  let active = null;
  let typeahead = '';
  let typedAt = 0;

  for (const menu of NETWORK_MENUS) {
    const key = `network-${menu.label.toLowerCase()}`;
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.dataset.menu = key;
    trigger.textContent = menu.label;
    networkBar.append(trigger);

    const popover = document.createElement('div');
    popover.className = 'menu-popover network-popover';
    popover.dataset.popover = key;
    menu.groups.forEach((group, groupIndex) => {
      if (groupIndex) {
        const separator = document.createElement('div');
        separator.className = 'menu-separator';
        popover.append(separator);
      }
      for (const name of group) {
        const href = webURL(APP_URLS[name]);
        const item = document.createElement(href ? 'a' : 'button');
        item.dataset.networkApp = name;
        const label = document.createElement('span');
        label.textContent = name;
        item.append(label);
        if (href) {
          item.href = href;
          item.target = '_blank';
          item.rel = 'noopener noreferrer';
          item.title = `Open ${name} in a new tab`;
        } else {
          item.type = 'button';
          if (name === 'DocDome') {
            item.dataset.command = 'open-docdome';
            item.title = 'Open the DocDome file browser';
          } else if (name === 'PicPlayer') {
            item.setAttribute('aria-current', 'page');
            const badge = document.createElement('small');
            badge.className = 'current-app-badge';
            badge.textContent = 'Current app';
            item.append(badge);
          } else {
            item.title = `${name} — destination not configured`;
          }
        }
        popover.append(item);
      }
    });
    popoverRoot.append(popover);
  }

  for (const [name, destination] of Object.entries(PROFILE_LINKS)) {
    const link = document.querySelector(`#profile-${name}`);
    const href = webURL(destination);
    if (!link || !href) continue;
    const url = new URL(href);
    if (name === 'login' || name === 'join') {
      url.searchParams.set('returnTo', location.pathname + location.search + location.hash);
    }
    link.href = url.href;
  }

  const triggers = [...document.querySelectorAll('[data-menu]')];
  const menus = new Map();
  for (const trigger of triggers) {
    const popover = popoverRoot.querySelector(`[data-popover="${trigger.dataset.menu}"]`);
    if (!popover) continue;
    trigger.id ||= `trigger-${trigger.dataset.menu}`;
    trigger.type = 'button';
    trigger.setAttribute('aria-haspopup', 'menu');
    trigger.setAttribute('aria-expanded', 'false');
    popover.id ||= `menu-${trigger.dataset.menu}`;
    trigger.setAttribute('aria-controls', popover.id);
    popover.setAttribute('role', 'menu');
    popover.setAttribute('aria-labelledby', trigger.id);
    popover.querySelectorAll('button, a').forEach((item) => {
      item.setAttribute('role', 'menuitem');
      item.tabIndex = -1;
    });
    popover.querySelectorAll('.menu-separator').forEach((separator) => {
      separator.setAttribute('role', 'separator');
      separator.setAttribute('aria-orientation', 'horizontal');
    });
    menus.set(trigger, popover);
  }
  document.querySelectorAll('.menu-bar').forEach((bar) => {
    bar.setAttribute('role', 'menubar');
    bar.setAttribute('aria-orientation', 'horizontal');
    bar.querySelectorAll('[data-menu]').forEach((trigger, index) => {
      trigger.setAttribute('role', 'menuitem');
      trigger.tabIndex = index === 0 ? 0 : -1;
    });
  });

  function items(popover) {
    return [...popover.querySelectorAll('[role="menuitem"]')].filter((item) =>
      !item.disabled && !item.closest('.hidden, [hidden]') && item.getClientRects().length,
    );
  }

  function close({ restoreFocus = false } = {}) {
    if (!active) return;
    const previous = active;
    active = null;
    previous.popover.classList.remove('open');
    previous.trigger.classList.remove('active');
    previous.trigger.setAttribute('aria-expanded', 'false');
    typeahead = '';
    if (restoreFocus) previous.trigger.focus({ preventScroll: true });
  }

  function position() {
    if (!active) return;
    const { trigger, popover } = active;
    const rect = trigger.getBoundingClientRect();
    const top = rect.bottom + 3;
    // Long lists scroll within the viewport, including in landscape mobile.
    popover.style.top = `${top}px`;
    popover.style.maxHeight = `${Math.max(0, window.innerHeight - top - 8)}px`;
    const width = popover.getBoundingClientRect().width;
    const preferredLeft = trigger === profileButton ? rect.right - width : rect.left;
    popover.style.left = `${Math.max(8, Math.min(preferredLeft, window.innerWidth - width - 8))}px`;
  }

  function open(trigger, focusItem = null) {
    const popover = menus.get(trigger);
    if (!popover) return;
    close();
    active = { trigger, popover };
    trigger.classList.add('active');
    trigger.setAttribute('aria-expanded', 'true');
    popover.classList.add('open');
    popover.scrollTop = 0;
    position();
    if (focusItem) {
      const choices = items(popover);
      (focusItem === 'last' ? choices.at(-1) : choices[0])?.focus({ preventScroll: true });
      if (focusItem === 'last') choices.at(-1)?.scrollIntoView({ block: 'nearest' });
    }
  }

  function focusTrigger(trigger) {
    const bar = trigger.closest('.menu-bar');
    if (bar) bar.querySelectorAll('[data-menu]').forEach((item) => { item.tabIndex = item === trigger ? 0 : -1; });
    trigger.focus({ preventScroll: true });
    trigger.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-menu]');
    if (menus.has(trigger)) {
      const wasOpen = active?.trigger === trigger && !active.openedByHover;
      focusTrigger(trigger);
      if (wasOpen) close();
      else open(trigger, event.detail === 0 ? 'first' : null);
      return;
    }
    const item = event.target.closest('.menu-popover [role="menuitem"]');
    if (!item) return;
    if (item.dataset.networkApp && !item.matches('a') && !item.dataset.command) {
      const name = item.dataset.networkApp;
      close({ restoreFocus: true });
      onNotice(name === 'PicPlayer' ? 'You are already in PicPlayer.' : `${name}: no destination URL has been configured yet.`);
    } else {
      close({ restoreFocus: true });
    }
  });

  // Menu-bar hover switches menus only after the user has opened one.
  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse') return;
    const trigger = event.target.closest('.menu-bar [data-menu]');
    if (menus.has(trigger) && active?.trigger !== trigger && active?.trigger.closest('.menu-bar')) {
      focusTrigger(trigger);
      open(trigger);
      active.openedByHover = true;
    }
  });
  document.addEventListener('pointerdown', (event) => {
    if (!event.target.closest('[data-menu], .menu-popover')) close();
  });
  document.addEventListener('focusin', (event) => {
    if (active && !active.popover.contains(event.target) && event.target !== active.trigger) close();
  });
  window.addEventListener('resize', position);
  window.visualViewport?.addEventListener('resize', position);
  document.querySelectorAll('.menu-bar').forEach((bar) => bar.addEventListener('scroll', position, { passive: true }));

  document.addEventListener('keydown', (event) => {
    const trigger = event.target.closest('[data-menu]');
    const inPopover = active?.popover.contains(event.target);
    if (!menus.has(trigger) && !inPopover && !active) return;
    const key = event.key;
    if (key === 'Escape' && active) {
      event.preventDefault(); event.stopPropagation(); close({ restoreFocus: true }); return;
    }
    if (key === 'Tab') {
      // Restore the trigger first so native Tab leaves this menu in header order.
      close({ restoreFocus: !!inPopover });
      return;
    }
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const currentTrigger = inPopover ? active.trigger : trigger;
    if (!menus.has(currentTrigger)) return;
    const bar = currentTrigger.closest('.menu-bar');
    if ((key === 'ArrowLeft' || key === 'ArrowRight') && bar) {
      const siblings = [...bar.querySelectorAll('[data-menu]')];
      const direction = key === 'ArrowRight' ? 1 : -1;
      const next = siblings[(siblings.indexOf(currentTrigger) + direction + siblings.length) % siblings.length];
      const wasOpen = !!active;
      event.preventDefault(); event.stopPropagation(); focusTrigger(next);
      if (wasOpen) open(next, 'first');
      return;
    }
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault(); event.stopPropagation();
      if (!inPopover) open(currentTrigger, key === 'ArrowUp' ? 'last' : 'first');
      else {
        const choices = items(active.popover);
        const index = choices.indexOf(document.activeElement);
        const direction = key === 'ArrowDown' ? 1 : -1;
        choices[(index + direction + choices.length) % choices.length]?.focus();
      }
      return;
    }
    if (key === 'Home' || key === 'End') {
      event.preventDefault(); event.stopPropagation();
      if (inPopover) {
        const choices = items(active.popover);
        (key === 'Home' ? choices[0] : choices.at(-1))?.focus();
      } else if (bar) {
        const siblings = [...bar.querySelectorAll('[data-menu]')];
        focusTrigger(key === 'Home' ? siblings[0] : siblings.at(-1));
      }
      return;
    }
    // Native Enter/Space activation remains intact; letter keys search the menu
    // instead of accidentally invoking image-editor tool shortcuts.
    if (inPopover && key.length === 1 && key !== ' ') {
      event.preventDefault(); event.stopPropagation();
      const now = Date.now();
      typeahead = now - typedAt < 700 ? typeahead + key.toLowerCase() : key.toLowerCase();
      typedAt = now;
      const choices = items(active.popover);
      const index = choices.indexOf(document.activeElement);
      const ordered = [...choices.slice(index + 1), ...choices.slice(0, index + 1)];
      let match = ordered.find((item) => item.textContent.trim().toLowerCase().startsWith(typeahead));
      if (!match) {
        typeahead = key.toLowerCase();
        match = ordered.find((item) => item.textContent.trim().toLowerCase().startsWith(typeahead));
      }
      match?.focus();
    }
  }, true);

  function updateProfile(session) {
    const signedIn = !!session.signedIn;
    const name = session.user?.displayName || session.user?.email || 'ZenID account';
    const label = signedIn ? `User profile — ${name}` : 'User profile — log in or join';
    profileButton.classList.toggle('is-signed-in', signedIn);
    profileButton.setAttribute('aria-label', label);
    profileButton.title = label;
    const initials = document.querySelector('#profile-initials');
    initials.textContent = Array.from(name.trim())[0]?.toUpperCase() || 'Z';
    initials.classList.toggle('hidden', !signedIn);
    document.querySelector('#profile-icon').classList.toggle('hidden', signedIn);
    document.querySelector('#profile-heading').textContent = signedIn ? name : 'Your account';
    document.querySelector('#profile-description').textContent = signedIn ? session.user?.email || 'Connected with ZenID' : 'Connect with ZenID';
    document.querySelector('#profile-guest-actions').classList.toggle('hidden', signedIn);
    document.querySelector('#profile-account-actions').classList.toggle('hidden', !signedIn);
    // A session refresh must not leave keyboard focus on a newly hidden action.
    if (active?.popover === profilePopover && document.activeElement.closest('.hidden')) {
      items(profilePopover)[0]?.focus();
    }
  }

  return { close, updateProfile };
}
